import {
  Input,
  Label,
  Select,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@attmos/flowstack-ui";
import { Info } from "lucide-react";

import { cn } from "@/lib/utils";

/** Mirror of flowstack.manifest.schema.Parameter (subset the form
 * generator switches on at render time). Read defensively from the
 * raw manifest dict — every field is optional because some
 * platform-injected parameters carry placeholders.
 *
 * ``float`` is the one type flowstack.flow.schema.FlowParameter admits
 * that the Tile-side Parameter does not (``schema.py:85`` vs
 * ``manifest/schema.py:245``). It is listed here because #831 mounts
 * this same form on the Flow submit route: without a case for it a
 * float Flow parameter would render a label and no control — visible,
 * unsettable, which is the exact defect that unit exists to remove. */
export interface ParameterShape {
  type: "enum" | "int" | "float" | "bool" | "string" | "quantity";
  default?: unknown;
  source?: "user" | "fanout" | "inherited";
  required?: boolean;
  description?: string;
  ui_visible?: boolean;
  expert?: boolean;
  values?: unknown[];
  range?: [number, number];
  canonical_unit?: string;
}

export interface ParameterFieldProps {
  name: string;
  parameter: ParameterShape;
  value: unknown;
  onChange: (value: unknown) => void;
  /** Read-only fields are rendered greyed out — used for source !=
   * 'user' (platform-injected) parameters that surface for visibility
   * but should not be editable. */
  readOnly?: boolean;
  className?: string;
}

/**
 * Single parameter row — label + control + optional description + range.
 *
 * Switches on ``parameter.type`` (enum / int / bool / string / quantity)
 * and on ``parameter.source`` (user → editable; fanout / inherited →
 * read-only chip). Respects ``ui_visible`` and ``expert`` flags at
 * the caller (this component renders whatever it's handed).
 */
export function ParameterField({
  name,
  parameter,
  value,
  onChange,
  readOnly = false,
  className,
}: ParameterFieldProps): JSX.Element {
  const id = `param-${name}`;
  const isPlatformInjected = Boolean(parameter.source && parameter.source !== "user");
  const effectivelyReadOnly = readOnly || isPlatformInjected;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center gap-2">
        <Label htmlFor={id} className="font-mono text-xs">
          {name}
        </Label>
        {parameter.required && (
          <span className="text-[10px] uppercase tracking-wider text-state-failed-validation-fg">
            required
          </span>
        )}
        {parameter.expert && (
          <span className="label-micro rounded-sm bg-surface-2 px-1 text-muted-foreground">
            expert
          </span>
        )}
        {isPlatformInjected && (
          <span className="label-micro rounded-sm bg-surface-2 px-1 text-muted-foreground">
            {parameter.source}
          </span>
        )}
        {parameter.description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Description: ${parameter.description}`}
              >
                <Info className="h-3 w-3" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent>{parameter.description}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {renderControl({ id, parameter, value, onChange, readOnly: effectivelyReadOnly })}
      {parameter.range && (parameter.type === "int" || parameter.type === "float") && (
        <span className="text-[10px] text-muted-foreground">
          range {parameter.range[0]}–{parameter.range[1]}
        </span>
      )}
    </div>
  );
}

interface ControlProps {
  id: string;
  parameter: ParameterShape;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly: boolean;
}

function renderControl({ id, parameter, value, onChange, readOnly }: ControlProps): JSX.Element {
  switch (parameter.type) {
    case "enum":
      // Per Rupert PR-260: enum values are typed list[Any] in the
      // manifest schema — numbers and bools are legal alongside
      // strings. Stringify only for display + the select's value
      // attribute; emit the ORIGINAL typed value on change so the
      // cost-preview / submit request keeps the manifest's type.
      return (
        <Select
          size="sm"
          id={id}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => {
            const selectedStr = e.target.value;
            const original = (parameter.values ?? []).find((v) => String(v) === selectedStr);
            onChange(original ?? selectedStr);
          }}
          disabled={readOnly}
        >
          {(parameter.values ?? []).map((v) => (
            <option key={String(v)} value={String(v)}>
              {String(v)}
            </option>
          ))}
        </Select>
      );
    // `float` shares the int control and differs only in `step`: a
    // spinner that steps by 1 on a continuous parameter is wrong, and
    // `step="any"` is also what lets a browser accept a fractional
    // entry without a validity warning. Emission semantics are
    // deliberately identical to int's — this case exists to give the
    // flow-only type a control, not to change how numbers submit.
    case "int":
    case "float":
      return (
        <Input
          id={id}
          type="number"
          step={parameter.type === "int" ? 1 : "any"}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          min={parameter.range?.[0]}
          max={parameter.range?.[1]}
          disabled={readOnly}
          className="font-mono tabular-nums"
        />
      );
    case "bool":
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            disabled={readOnly}
            className="h-4 w-4 rounded border-border-subtle text-cyan focus-visible:ring-ring disabled:opacity-50"
          />
          <span className="text-muted-foreground">{value === true ? "true" : "false"}</span>
        </label>
      );
    case "string":
      return (
        <Input
          id={id}
          type="text"
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          className="font-mono"
        />
      );
    case "quantity":
      return (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="number"
            step="any"
            value={
              typeof value === "object" && value !== null && "val" in value
                ? String((value as { val: unknown }).val)
                : value === undefined || value === null
                  ? ""
                  : String(value)
            }
            onChange={(e) => {
              if (e.target.value === "") {
                onChange(null);
                return;
              }
              const numeric = Number(e.target.value);
              // Per Rupert PR-260: intermediate typing states like
              // "-" or "." make Number(...) NaN, which JSON.stringify
              // turns into `null` — the API would briefly receive a
              // malformed quantity. Skip the emit until the input
              // parses to a finite number.
              if (!Number.isFinite(numeric)) return;
              onChange({
                val: numeric,
                unit: parameter.canonical_unit ?? "dimensionless",
              });
            }}
            min={parameter.range?.[0]}
            max={parameter.range?.[1]}
            disabled={readOnly}
            className="font-mono tabular-nums"
          />
          <span className="rounded-sm bg-surface-2 px-2 py-1 font-mono text-xs text-muted-foreground">
            {parameter.canonical_unit ?? "dimensionless"}
          </span>
        </div>
      );
  }
}
